<script lang="ts" setup>
import type { IAppProps } from '@/interfaces/IAppProps'
import type { BonusWalletData } from '@/interfaces/IBonusWallet'
import { storeToRefs } from 'pinia'
import { onMounted, onUnmounted, ref, watchEffect } from 'vue'
import { accountBalanceApi, balanceApi } from '@/apis/balanceApi'
import BonusWallet from '@/components/bonus-wallet/BonusWallet.vue'
import IconArrowExpandable from '@/components/icons/IconArrowExpandable.vue'
import { useEntryProps } from '@/composable/useDatasetProps'
import { useGlobalStore } from '@/stores/globalStore'
import { useUserStore } from '@/stores/userStore'

const globalStore = useGlobalStore()
const userStore = useUserStore()
const { userInfo } = storeToRefs(userStore)

const balance = ref<string>('')
const isExpanded = ref(false)
const navListTop = ref<number>(0)
const bonusWalletData = ref<BonusWalletData | null>(null)
const expandableContentRight = ref<number>(0)

// Mock data to ensure props are used
const mockBonusWalletData: BonusWalletData = {
    mainWalletBalance: 100000,
    bonusWalletBalance: 0,
    bonusItems: [],
}

const { loginName } = useEntryProps<IAppProps>()

const updateNavListPosition = () => {
    const navList = document.querySelector('.nav-list') as HTMLElement
    if (navList) {
        navListTop.value = navList.getBoundingClientRect().top
    }
}

const updateExpandableContentPosition = () => {
    if (window.innerWidth >= 1024) {
        const arrowToggle = document.querySelector('.arrow-toggle') as HTMLElement
        if (arrowToggle) {
            const rect = arrowToggle.getBoundingClientRect()
            const windowWidth = window.innerWidth
            expandableContentRight.value = windowWidth - rect.right
        }
    }
    else {
        expandableContentRight.value = 0
    }
}

const fetchBonusWalletData = async () => {
    try {
        if (userInfo.value.customerId) {
            const fullBalance = await accountBalanceApi.get(userInfo.value.customerId)
            const totalBonusBalance = (fullBalance.bonusWalletBalances || []).reduce(
                (sum, wallet) => sum + wallet.cashBalance,
                0,
            )

            bonusWalletData.value = {
                mainWalletBalance: 100000,
                bonusWalletBalance: totalBonusBalance,
                bonusItems: [
                    {
                        id: 'sports-cashback',
                        title: 'Sports Cashback',
                        currentAmount: 3600,
                        targetAmount: 15000,
                        redeemableAmount: 1000,
                        progressPercentage: 24,
                    },
                    {
                        id: 'games-welcome-bonus',
                        title: '100% Games Welcome Bonus',
                        currentAmount: 3600,
                        targetAmount: 15000,
                        redeemableAmount: 1000,
                        progressPercentage: 24,
                    },
                    {
                        id: 'casino-reload-bonus',
                        title: '20% Casino Reload Bonus',
                        currentAmount: 3600,
                        targetAmount: 15000,
                        redeemableAmount: 1000,
                        progressPercentage: 24,
                    },
                ],
            }
        }
    }
    catch (error) {
        console.error('Failed to fetch bonus wallet data:', error)
    }
}

const toggleExpand = () => {
    isExpanded.value = !isExpanded.value
    if (isExpanded.value) {
        updateNavListPosition()
        updateExpandableContentPosition()
        if (!bonusWalletData.value) {
            fetchBonusWalletData()
        }
    }
}

onMounted(() => {
    updateNavListPosition()
    updateExpandableContentPosition()
    window.addEventListener('resize', () => {
        updateNavListPosition()
        updateExpandableContentPosition()
    })
    window.addEventListener('scroll', () => {
        updateNavListPosition()
        updateExpandableContentPosition()
    })
})

onUnmounted(() => {
    window.removeEventListener('resize', updateNavListPosition)
    window.removeEventListener('scroll', updateNavListPosition)
})

watchEffect(() => {
    if (isExpanded.value) {
        updateNavListPosition()
        updateExpandableContentPosition()
    }
})

const reloadBalance = async () => {
    const balanceText = document.getElementById('balance-txt')
    balanceText?.classList.remove('blink')

    const refreshIcon = document.getElementById('icon-refresh')
    refreshIcon?.classList.toggle('icon-spin')

    balance.value = await balanceApi.get()
    refreshIcon?.classList.toggle('icon-spin')
    balanceText?.classList.toggle('blink')
}
const autoReloadBalance = async () => {
    setInterval(async () => {
        const newBalance = await balanceApi.get()
        if (balance.value !== newBalance) {
            balance.value = newBalance
        }
    }, 30000)
};

(async () => {
    const newBalance = await balanceApi.get()
    balance.value = newBalance
    globalStore.setBalance(newBalance)
    await autoReloadBalance()
})()
</script>

<template>
    <div class="user-info">
        <div class="login-name">
            <img alt="user-icon" class="icon-user" src="../../assets/images/icon_user.svg">
            <span class="username-txt">{{ loginName }}</span>
        </div>
        <div class="balance-container">
            <div class="balance balance-refresh" :class="{ 'show-dropdown-arrow': isExpanded }" @click="reloadBalance()">
                <span id="balance-txt" class="balance-txt">
                    <span id="balance">{{ balance }}</span>
                </span>
                <div v-if="globalStore.mainToggle.toggleDisplayBonusWalletAmount" class="arrow-toggle" @click.stop="toggleExpand">
                    <IconArrowExpandable :expanded="isExpanded" />
                </div>
                <img id="icon-refresh" alt="reload" class="icon-refresh" src="../../assets/images/icon_reload.svg">
            </div>
            <div v-show="isExpanded" class="expandable-content" :style="{ top: `${navListTop}px`, right: expandableContentRight > 0 ? `${expandableContentRight}px` : 'auto' }">
                <BonusWallet :data="bonusWalletData || mockBonusWalletData" />
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@use "@/assets/styles/common/variables.scss" as *;

.user-info {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    line-height: 16px;

    .login-name,
    .balance {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
    }

    .balance {
        position: relative;
        cursor: pointer;

        &.show-dropdown-arrow::after {
            position: absolute;
            z-index: 5;
            bottom: -15px;
            left: 50%;
            width: 10px;
            height: 10px;
            box-sizing: border-box;
            border-top: 10px solid $indigo-100;
            border-left: 10px solid $indigo-100;
            content: "";
            transform: translateX(-50%) rotate(45deg);
        }
    }
}

.username-txt {
    display: inline-block;
    color: #ffffff;
    font-weight: bold;
}

.icon-user {
    width: 16px;
}

.balance-container {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.balance-refresh:hover span,
.balance-refresh:hover img {
    color: #ffffff;
}

.balance-refresh.refresh-loading:hover span,
.balance-refresh.refresh-loading:hover img {
    color: #ffffff;
}

.balance-refresh .icon-refresh {
    width: 16px;
}

.arrow-toggle {
    display: flex;
    align-items: center;
    padding: 0 4px;
    color: #ffffff;
    cursor: pointer;
}

.balance-txt {
    display: inline-block;
    color: #ffffff;
    font-weight: bold;
}

.expandable-content {
    position: fixed;
    z-index: 4;
    left: 50%;
    display: flex;
    overflow: hidden;
    width: calc(100vw - 32px);
    max-width: 400px;
    flex-direction: column;
    align-items: center;
    padding: 0;
    border-radius: 8px;
    background-color: #ffffff;
    box-shadow: 0 0 12px 0 rgb(0, 0, 0, 16%), 0 0 4px 0 rgb(0, 0, 0, 12%);
    transform: translateX(-50%);
}

@media (min-width: $web-width) {
    .expandable-content {
        left: auto;
        width: 400px;
        transform: none;
    }
}

.icon-spin {
    display: inline-block;
    animation: spin 2s infinite linear;
}

.blink {
    animation: blink 1s;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(359deg);
    }
}

@keyframes blink {
    0% {
        opacity: 1;
    }

    50% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
}
</style>
